import _ from 'lodash';
import * as qs from 'qs';
import { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';

import { useLocation } from '../../../contexts/RouterContext';
import { window } from '../../../globals';
import { permittedIssueCategoriesToView } from '../../../helpers/issuesV2';
import { useIssueCategorySummary } from '../../../hooks/api/issues/useIssueCategorySummary';
import { useCurrentProject } from '../../../hooks/api/projects/useCurrentProject';
import { useCurrentRevision } from '../../../hooks/api/revisions/useCurrentRevision';
import * as IssuesV2Store from '../../../redux/issuesV2';
import TeamsModule from '../../../redux/teams';
import useSetIssuesScope from '../../issues/IssuesInbox/hooks/useSetIssuesScope';
import type { Category } from '../../../../shared/issuesV2';

type Props = {
  baseRevisionURL: string;
  revisionScanId?: number;
  projectLocator: string;
  revisionId: string | null;
};

/**
 * Hook that returns props for the subnav item for project issues.
 * This mostly just interfaces with Redux, setting the scope and fetching
 * the issue category summary. In addition, it determines which subnav items
 * can actually be displayed, based on the users permissions.
 */
export function useProjectIssuesSubnav({ baseRevisionURL, revisionScanId, projectLocator, revisionId }: Props) {
  const location = useLocation();
  //  Ensure that the scope is correctly set, based on the params.
  //  Returns the scope so it can be passed to the other hook.
  const scope = useSetIssuesScope({
    project_id: projectLocator,
    revision_id: revisionId!,
    revisionScanId,
  });

  const issueCategory = useSelector(IssuesV2Store.selectors.getIssueCategory);
  const teamIds = useSelector(TeamsModule.selectors.getTeamSelection);
  // Wait until the project (always enabled) *and* the revision (only enabled
  // when the route includes a `revision_id`) have finished their initial load.
  // `isInitialLoading` is used rather than `isLoading` so that disabled queries
  // — e.g. `useCurrentRevision` on Activity/Settings — don't keep the gate
  // closed; `isLoading` is `true` for disabled queries in React Query v4.
  const { isInitialLoading: isProjectLoading } = useCurrentProject();
  const { isInitialLoading: isRevisionLoading } = useCurrentRevision();
  const shouldFetch = !isProjectLoading && !isRevisionLoading;
  const {
    data: categorySummary,
    error: categorySummaryError,
    isError: isCategorySummaryError,
  } = useIssueCategorySummary(scope, teamIds, shouldFetch);
  const totalIssuesCount = useMemo(
    () => (categorySummary ? Object.values(categorySummary).reduce((total, count) => total + count, 0) : null),
    [categorySummary],
  );
  const getProjectIssuesURL = useCallback(
    (category?: Category): string => {
      const query = _.isNil(revisionScanId) ? '' : qs.stringify({ revisionScanId }, { addQueryPrefix: true }); // Adds the leading "?"
      return category ? `${baseRevisionURL}/issues/${category}${query}` : `${baseRevisionURL}/issues${query}`;
    },
    [baseRevisionURL, revisionScanId],
  );

  //  Recalculate the permitted categories only if user permissions actually change.
  // eslint-disable-next-line react-hooks/exhaustive-deps -- This is fine.
  const permittedCategories = useMemo(() => permittedIssueCategoriesToView(), [window.user?.permissions]);

  const overviewPath = `${baseRevisionURL}/issues/overview`;
  const isActive = location.pathname.startsWith(`${baseRevisionURL}/issues`);
  const isOverview = location.pathname === overviewPath;

  // The count should be the current category if the issues page is active, otherwise the total.
  // The issue category from the store is always available, so that alone is not reliable.
  const issueCount = isActive && !isOverview
    ? (categorySummary?.[issueCategory] ?? null)
    : totalIssuesCount;

  return {
    issueCount,
    categorySummary,
    categorySummaryError,
    isCategorySummaryError,
    totalIssuesCount,
    permittedCategories,
    getProjectIssuesURL,
  };
}
